import {
    AuthConfig,
    Callbacks,
    Components,
    Settings,
    State,
    User,
    BackendSF,
    Model,
    MessageInfo
} from 'aq-data-management';
import PDFValidator from '../../components/PDFViewer/PDFValidator';
import useAnalytics from '../../hooks/useAnalytics';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useDownloadSource, useFeedbackCategories, useKAModal, useScopSettings, useTenant, useThreadsHandling } from '../../AppContext';
import { useAskStream } from '../../context/AskStreamContext';
import SFBackend from '../../backend/SFBackend';
import { ResponseDepth } from '../../types';
import { getAggregatedResultDetails } from '../KASection/KASectionHelpers';

const useDataManagementProps = () => {
    const { handleShowSourceDocumentDetails, getIsSourceActive } = useKAModal();
    const { downloadSource, downloadInProgressFileList } = useDownloadSource();
    const { sendEvent } = useAnalytics();
    const { onSelectedThread } = useThreadsHandling();
    const { dummyAskStream } = useAskStream();
    const { enableSourceImportByAdminAndExpert, allowUserToEditInExpertEditTable } = useScopSettings();
    const { feedbackCategories } = useFeedbackCategories();
    const { tenantId } = useTenant();
    const { username } = useSelector((state: any) => state.globalReducer);
    const { email, fullUserName } = useSelector((state: any) => state.userProfileReducer);

    const user: User = useMemo(
        () => ({
            tenantId,
            fullName: fullUserName,
            username,
            email,
            translations: {}
        }),
        [tenantId, fullUserName, username, email]
    );

    const settings: Settings = useMemo(
        () => ({
            enableSourceImportByAdminAndExpert,
            allowUserToEditInExpertEditTable,
            feedbackCategories: feedbackCategories.chips
        }),
        [
            enableSourceImportByAdminAndExpert,
            allowUserToEditInExpertEditTable,
            feedbackCategories.chips
        ]
    );

    const callbacks: Callbacks = useMemo(
        () => ({
            sendEvent,
            loadExistingSession: (chatId: string, model: string, productId: string, question: string, investigationId?: string, workOrder?: string) => {
                onSelectedThread(chatId, model, productId, investigationId, false);
            },
            dummyAskStream: (question: string, model: Model, setUpdatedMessage: (message: MessageInfo) => void, setIsStreamInProgress: (isStreamInProgress: boolean) => void, responseDepth?: ResponseDepth, options?: {
                onAskInit?: (abortController: AbortController) => void;
                onStreamInit?: (streamReader: ReadableStreamDefaultReader<Uint8Array>) => void;
                onError?: () => void;
            }) => {
                dummyAskStream(question, model.knowledgeModel, setUpdatedMessage, setIsStreamInProgress, responseDepth, options);
            },
            downloadSource,
            viewSource: (source: any) => {
                const { fileType } = getAggregatedResultDetails([source]);
                handleShowSourceDocumentDetails(0, [source], fileType);
            },
            getIsSourceOpen: getIsSourceActive
        }),
        [
            sendEvent,
            onSelectedThread,
            dummyAskStream,
            downloadSource,
            handleShowSourceDocumentDetails,
            getIsSourceActive
        ]
    );

    const authConfig: AuthConfig = {
        sfSessionId: window.__sfdcIds?.sessionId || '',
        genAIBaseUrl: window.__triageStore?.scopServerBaseUrl || '',
        isSandboxOrg: window.__triageStore?.isSandboxOrg || false
    };

    const state: State = useMemo(
        () => ({
            downloadInProgressFiles: downloadInProgressFileList
        }),
        [downloadInProgressFileList]
    );

    const components: Components = {
        PDFValidator
    };

    const backendSF: BackendSF =
      {
            getAdditionalChatHistoryData: SFBackend.getAdditionalChatHistoryData,
            getExistingUsersFromList: SFBackend.getExistingUsersFromList,
            fetchCatalogProduct: SFBackend.fetchCatalogProduct
        }

    return {
        user,
        settings,
        callbacks,
        authConfig,
        state,
        components,
        backendSF
    };
};

export default useDataManagementProps;
